import React, { useEffect, useState } from 'react'
import { PET_TYPES } from '@cobasi/cobasi-utils/constants'
import { getTypeLabel } from '@cobasi/cobasi-utils/helpers/pet'
import moment from 'moment'

import Input from '../../../../../../components/Form/Input'
import Button from '../../../../../../components/Form/Button'
import Radio from '../../../../../../components/Form/Radio'
import ToggleInput from '../../../../../../components/Form/ToggleInput'

import useForm from './hooks/useForm'

import * as S from './styles'
import { getBreeds } from '../../../../../../services/pet'
import SearchSelect from '../../../../../../components/Form/SearchSelect'
import Alert from '../../../../../../components/Form/Alert'
import { DEFAULT_VALUES } from './hooks/constants'

const NewPetForm = ({ tutorData = undefined, petData = undefined, visibility = false, setVisibility = undefined, updated, alert = undefined }) => {
  const [loading, setLoading] = useState(false)
  const [breeds, setBreeds] = useState([])
  const { errors, clearErrors, watch, register, reset, setValue, setError, submitForm } = useForm()
  const isEditing = !!petData
  const id = petData?.id || undefined
  const petTypeList = Object.values(PET_TYPES).map(type => ({ value: type, label: getTypeLabel(type) }))

  useEffect(() => {
    if (!visibility && !isEditing) {
      reset(DEFAULT_VALUES)
      setBreeds([])
    } else if (!visibility && isEditing) {
      const { gender, castrated, name, breed, weight, birth, temperament } = petData
      reset({
        name,
        gender,
        castrated,
        weight,
        ...(birth ? { birth: moment(birth).format('DD/MM/YYYY') } : { birth: '' }),
        temperament,
        specie: breed?.type,
        breed: breed?.id
      })
    }
  }, [visibility])

  useEffect(() => {
    if (watch.specie) {
      handleGetBreeds()
    }
  }, [watch.specie])

  useEffect(() => {
    const pattern = /^\d{2}\/\d{2}\/\d{4}/
    const isValidDate = moment(watch.birth, 'DD/MM/YYYY').isValid()
    if (pattern.test(watch.birth)) {
      if (!isValidDate) {
        setError('birth', {
          type: 'manual',
          message: 'Digite uma data válida'
        })
      } else {
        clearErrors('birth')
      }
    }

    if (isEditing && !isValidDate && petData.birth) {
      setError('birth', {
        type: 'manual',
        message: 'Digite uma data válida'
      })
    }
  }, [watch.birth])

  useEffect(() => {
    if (isEditing) {
      const { gender, castrated, name, breed, weight, birth, temperament } = petData
      reset({
        name,
        gender,
        castrated,
        weight,
        ...(birth && { birth: moment(birth).format('DD/MM/YYYY') }),
        temperament,
        specie: breed?.type,
        breed: breed?.id
      })
    }
  }, [isEditing, petData])

  const handleGetBreeds = async () => {
    try {
      setLoading(true)
      const req = await getBreeds(watch.specie)
      if (req.length) {
        setBreeds(req.map(({ id, name }) => ({ value: id, label: name })))
      }
      setLoading(false)
    } catch (error) {
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    let owner
    if (tutorData) {
      const { id: ownerId, email: ownerEmail, document: ownerCpf, firstName, lastName } = tutorData
      owner = { ownerId, ownerEmail, ownerCpf, ownerName: firstName.concat(' ', lastName) }
    } else {
      const { ownerId, ownerEmail, ownerCpf, ownerName } = petData
      owner = { ownerId, ownerEmail, ownerCpf, ownerName }
    }
    if (!errors?.birth?.message) {
      submitForm({ owner, setLoading, isEditing, id, updated, setVisibility, alert })
    }
  }

  return (
    <S.Form onSubmit={(e) => handleSubmit(e)}>
      <S.ScrollWrapper>
        <S.InputWrapper>
          <S.Col>
            <S.Label>Sexo</S.Label>
            <S.RadioWrapper>
              <Radio
                label='Fêmea'
                name='gender'
                value='FEMALE'
                hookForm={register('gender', {
                  required: true
                })}
                errorMessage={errors?.gender?.message}
              />
              <Radio
                label='Macho'
                name='gender'
                value='MALE'
                hookForm={register('gender', {
                  required: true
                })}
                errorMessage={errors?.gender?.message}
              />
            </S.RadioWrapper>
          </S.Col>
          <S.Col gap='4px' >
            <ToggleInput
              checked={watch.castrated === true}
              onChange={e => setValue('castrated', e.target.checked)}
            />
            <S.Label
              size='14px'
              weight={400}
            >Castrado</S.Label>
          </S.Col>
        </S.InputWrapper>
        {errors?.gender?.message &&
          <S.AlertWrappper>
            <Alert
              show={errors?.gender?.message}
              type='danger'
            >
              É obrigatório informar o sexo.
            </Alert>
          </S.AlertWrappper>
        }
        <Input
          autoComplete='off'
          marginBottom='10px'
          label='Nome do pet (obrigatório)'
          name='name'
          placeholder='Digite o nome do pet'
          value={watch.name}
          errorMessage={errors?.name?.message}
          hookForm={register('name', {
            required: true
          })}
        />
        <S.InputWrapper
          marginBottom='10px'
        >
          <SearchSelect
            label='Espécie (obrigatório)'
            placeholder='Selecione a espécie'
            isSearchable={false}
            arrowColor='#777777'
            name='specie'
            arrowSize='18'
            value={petTypeList.find(type => type.value === watch.specie) || null}
            options={petTypeList}
            onChange={(specie) => setValue('specie', specie.value) && setValue('breed', null)}
            errorMessage={errors?.specie?.message}
          />
          <SearchSelect
            label='Raça (obrigatório)'
            placeholder='Selecione a raça'
            name='breed'
            noOptionsMessage={() => 'Antes selecione uma espécie'}
            value={breeds.find(breed => breed.value === watch.breed) || null}
            options={breeds}
            onChange={(breed) => setValue('breed', breed.value)}
            errorMessage={errors?.breed?.message}
          />
        </S.InputWrapper>
        <S.InputWrapper
          inputNoMargin
        >
          <Input
            autoComplete='off'
            marginBottom='10px'
            label='Data de nascimento'
            name='birth'
            placeholder='DD/MM/AAAA'
            pattern='\d{2}\/\d{2}\/\d{4}'
            mask='99/99/9999'
            maskChar={null}
            value={watch.birth}
            errorMessage={errors?.birth?.message}
            onChange={(e) => {
              setValue('birth', e.target.value)
            }}
          />
          <Input
            autoComplete='off'
            marginBottom='10px'
            label='Peso (Kg)'
            name='weight'
            placeholder='Digite o peso em kg'
            value={watch.weight || ''}
            errorMessage={errors?.weight?.message}
            onChange={(e) => {
              const input = e.target.value
              const regex = /^\d{0,3}([\,\.]\d{0,2})?$/
              if (regex.test(input)) {
                const sanitizedInput = input.replace(',', '.')
                setValue('weight', sanitizedInput)
              }
            }}
          />
        </S.InputWrapper>
        <S.InputWrapper>
          <S.Col>
            <S.Label>Temperamento</S.Label>
            <S.RadioWrapper>
              <Radio
                label='Manso'
                name='temperament'
                value='TAME'
                hookForm={register('temperament', {
                  required: true
                })}
                errorMessage={errors?.temperament?.message}
              />
              <Radio
                label='Bravo'
                name='temperament'
                value='AGGRESSIVE'
                hookForm={register('temperament', {
                  required: true
                })}
                errorMessage={errors?.temperament?.message}
              />
            </S.RadioWrapper>
          </S.Col>
        </S.InputWrapper>
        {errors?.temperament?.message &&
          <S.AlertWrappper>
            <Alert
              show={errors?.temperament?.message}
              type='danger'
            >
              É obrigatório informar o temperamento.
            </Alert>
          </S.AlertWrappper>
        }
      </S.ScrollWrapper>
      <S.Actions>
        <Button type='button' secondary color='#028BB7' onClick={() => setVisibility(false)}>fechar</Button>
        <S.SpinnerButton color='#028BB7' loading={loading}>
          <S.SpinnerButtonText>
            {isEditing
              ? 'Salvar alterações'
              : 'Salvar e cadastrar'
            }
          </S.SpinnerButtonText>
          <S.Spinner />
        </S.SpinnerButton>
      </S.Actions>
    </S.Form>
  )
}

export default NewPetForm
